import { useState, useEffect, useRef } from "react";
import {
  WorkspaceForm,
  useWorkspaces,
  useDeleteWorkspace,
  type WorkspaceWithRole,
} from "@project-mgmt/feature-workspaces";
import { Button, Modal, ConfirmDialog, Page, Container } from "@project-mgmt/ui";
import { useNavigate, useLocation } from "react-router-dom";
import { notify } from "../providers/ToastProvider";
import { routes } from "../utils/routes";
import { getLastWorkspaceId, setLastWorkspaceId } from "../utils/lastWorkspace";

/**
 * Completely standalone workspace page with NO integration system interference
 * This bypasses ALL integration components, providers, and orchestrators
 */
export function WorkspacesPage() {
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [editingWorkspace, setEditingWorkspace] = useState<WorkspaceWithRole | null>(null);
  const [deletingWorkspace, setDeletingWorkspace] = useState<WorkspaceWithRole | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const { data: workspacesData, isLoading, refetch } = useWorkspaces();
  const deleteWorkspace = useDeleteWorkspace();
  const navigate = useNavigate();
  const location = useLocation();
  const hasAutoRestored = useRef(false);

  const workspaces = workspacesData?.workspaces || [];

  // Auto-restore last workspace on mount (only once, and only if not explicitly navigating to workspaces page)
  useEffect(() => {
    // Skip auto-restore if:
    // 1. Already restored once
    // 2. User explicitly navigated here (location state indicates intentional visit)
    // 3. Still loading or no workspaces
    if (hasAutoRestored.current || location.state?.skipAutoRestore || isLoading || workspaces.length === 0) {
      return;
    }

    const lastWorkspaceId = getLastWorkspaceId();
    if (lastWorkspaceId) {
      // Check if the last workspace still exists in the user's workspaces
      const workspaceExists = workspaces.some((w) => w.id === lastWorkspaceId);
      if (workspaceExists) {
        hasAutoRestored.current = true;
        navigate(routes.workspace(lastWorkspaceId), { replace: true });
      }
    }
  }, [isLoading, workspaces, navigate, location.state]);

  const handleCreateSuccess = async (workspace: WorkspaceWithRole) => {
    setShowCreateForm(false);
    notify.success(`Workspace "${workspace.name}" created successfully!`);
    await refetch();
    setLastWorkspaceId(workspace.id);
    navigate(routes.workspace(workspace.id));
  };

  const handleEditSuccess = async (workspace: WorkspaceWithRole) => {
    setShowEditForm(false);
    setEditingWorkspace(null);
    notify.success(`Workspace "${workspace.name}" updated successfully!`);
    await refetch();
  };

  const handleDeleteClick = (workspace: WorkspaceWithRole) => {
    setDeletingWorkspace(workspace);
  };

  const handleDeleteConfirm = async () => {
    if (!deletingWorkspace) return;

    setIsDeleting(true);
    const workspaceName = deletingWorkspace.name;

    try {
      await deleteWorkspace.trigger(deletingWorkspace.id);
      notify.success(`Workspace "${workspaceName}" deleted successfully!`);
      setDeletingWorkspace(null);
      await refetch();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Failed to delete workspace";
      notify.error(`Failed to delete "${workspaceName}": ${errorMessage}`);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeletingWorkspace(null);
  };

  return (
    <Container size="xl">
      <Page className="brand-page">
        <section className="brand-page-hero mb-6 text-center">
          <p className="brand-page-kicker">Workspace Hub</p>
          <h1 className="brand-page-title">
            Manage Your <span className="brand-page-title-accent">Workspaces</span>
          </h1>
          <p className="brand-page-subtitle">
            {workspaces.length > 0
              ? "Select a workspace to get started or create a new one"
              : "Get started by creating your first workspace"}
          </p>
        </section>

        {workspaces.length > 0 && (
          <div className="mb-6 flex items-center justify-between">
            <div />
            <Button
              onClick={() => {
                setShowCreateForm(true);
              }}
            >
              Create Workspace
            </Button>
          </div>
        )}

        {/* Main content */}
        <div className="brand-section-card p-6">
          <div className="mb-6 flex items-center justify-between">
            <h2 className="text-textPrimary text-lg font-semibold">Your Workspaces ({workspaces.length})</h2>
          </div>

          {isLoading ? (
            <div className="py-12 text-center">
              <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center">
                <div className="h-12 w-12 animate-spin rounded-full border-4 border-gray-200 border-t-blue-600"></div>
              </div>
              <p className="text-textSecondary">Loading workspaces...</p>
            </div>
          ) : workspaces.length === 0 ? (
            <div className="py-12 text-center">
              <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-gray-100">
                <svg className="h-8 w-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-4m-5 0H9m0 0H5m0 0h2M7 7h10M7 11h10M7 15h10"
                  />
                </svg>
              </div>
              <h3 className="text-textPrimary mb-2 text-lg font-medium">No workspaces yet</h3>
              <p className="text-textSecondary mb-4">Create your first workspace to get started</p>
              <Button onClick={() => setShowCreateForm(true)}>Create Your First Workspace</Button>
            </div>
          ) : (
            <div className="space-y-8">
              {workspaces.map((workspace) => (
                <div key={workspace.id} className="brand-panel flex items-center justify-between">
                  <div className="flex-1">
                    <div className="mb-1 flex items-center gap-2">
                      <h3 className="text-textPrimary font-medium">{workspace.name}</h3>
                      <span
                        className={`rounded-full px-2 py-1 text-xs ${
                          workspace.isOwner ? "brand-badge-owner" : "brand-badge-neutral"
                        }`}
                      >
                        {workspace.isOwner ? "Owner" : workspace.role}
                      </span>
                    </div>
                    {workspace.description && <p className="text-textSecondary text-sm">{workspace.description}</p>}
                    <p className="mt-1 text-xs text-textSecondary">
                      Created {new Date(workspace.createdAt).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setLastWorkspaceId(workspace.id);
                        navigate(routes.workspace(workspace.id));
                      }}
                    >
                      Open
                    </Button>
                    {workspace.isOwner && (
                      <>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setEditingWorkspace(workspace);
                            setShowEditForm(true);
                          }}
                        >
                          Edit
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDeleteClick(workspace)}
                          className="text-red-600 hover:text-red-700"
                        >
                          Delete
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Create Workspace Modal */}
      <Modal
        isOpen={showCreateForm}
        onClose={() => {
          setShowCreateForm(false);
        }}
        title="Create New Workspace"
      >
        <div className="p-4">
          <WorkspaceForm
            onSuccess={handleCreateSuccess}
            onCancel={() => {
              setShowCreateForm(false);
            }}
          />
        </div>
      </Modal>

      {/* Edit Workspace Modal */}
      <Modal
        isOpen={showEditForm}
        onClose={() => {
          setShowEditForm(false);
          setEditingWorkspace(null);
        }}
        title="Edit Workspace"
      >
        {editingWorkspace && (
          <div className="p-4">
            <WorkspaceForm
              workspace={editingWorkspace}
              onSuccess={handleEditSuccess}
              onCancel={() => {
                setShowEditForm(false);
                setEditingWorkspace(null);
              }}
            />
          </div>
        )}
      </Modal>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={!!deletingWorkspace}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title="Delete Workspace"
        message={`Are you sure you want to delete "${deletingWorkspace?.name}"? This action cannot be undone and will permanently remove all projects, tasks, and data associated with this workspace.`}
        confirmText="Delete Workspace"
        cancelText="Cancel"
        variant="danger"
        isLoading={isDeleting}
      />
      </Page>
    </Container>
  );
}
